import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { RoleService } from '../services/role.service';
import { ATTR } from './attrs';
import { loadDefaultCatalogue } from './catalogue';
import { noopHelpLogger, normalizePath } from './logger';
import { resolveAtPoint, resolveBox, resolveElement, resolveSectionAtPoint, resolveSectionOf } from './resolve';
import { createRoleDirectory } from './roles';
import { Box, HelpCatalogue, HelpLogger, HelpTarget, Resolution, RoleDirectory, SectionRef } from './types';

/** Movement (px) before a mouse press becomes a box selection instead of a section click. */
export const DRAG_THRESHOLD = 6;

export interface HelpContextValue {
    active: boolean;
    toggle: () => void;
    exit: () => void;
    /** Touch mode: tapping the page explains the one thing tapped (no drag boxes). Auto-detected, can be toggled. */
    touchMode: boolean;
    setTouchMode: (on: boolean) => void;
    target: HelpTarget | null;
    /** Live selection box while dragging. */
    dragBox: Box | null;
    catalogue: HelpCatalogue | null;
    catalogueError: boolean;
    /** `all` = the user must hold every key (HelpEntry.needsAllKeys); otherwise any one is enough. */
    canUse: (keys: string[] | undefined, all?: boolean) => boolean;
    roleDirectory: RoleDirectory;
    /** Drop-the-? handle and "tap target" both end here. */
    pickAtPoint: (x: number, y: number) => void;
    selectFeature: (el: HTMLElement) => void;
    /** Keyboard path: explain exactly this tagged element. */
    explainElement: (el: HTMLElement) => void;
    explainSection: (s: SectionRef) => void;
    clearTarget: () => void;
}

const HelpContext = createContext<HelpContextValue | null>(null);

export const useHelp = (): HelpContextValue => {
    const v = useContext(HelpContext);
    if (!v) throw new Error('useHelp must be used inside <HelpProvider>');
    return v;
};

/** Safe variant for components that may render outside the provider (returns null). */
export const useHelpOptional = (): HelpContextValue | null => useContext(HelpContext);

export const detectTouch = (): boolean =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;

interface Props {
    children: React.ReactNode;
    /** Test seams. */
    loadCatalogue?: () => Promise<HelpCatalogue>;
    roleDirectory?: RoleDirectory;
    logger?: HelpLogger;
}

export const HelpProvider: React.FC<Props> = ({ children, loadCatalogue = loadDefaultCatalogue, roleDirectory, logger = noopHelpLogger }) => {
    const { hasPermission, selectedInstitutionId } = useAuth();
    const [active, setActive] = useState(false);
    const [touchMode, setTouchMode] = useState<boolean>(detectTouch);
    const [target, setTarget] = useState<HelpTarget | null>(null);
    const [dragBox, setDragBox] = useState<Box | null>(null);
    const [catalogue, setCatalogue] = useState<HelpCatalogue | null>(null);
    const [catalogueError, setCatalogueError] = useState(false);

    const catRef = useRef<HelpCatalogue | null>(null);
    catRef.current = catalogue;
    const loggedRef = useRef<Set<string>>(new Set());
    const instRef = useRef<string | null>(selectedInstitutionId);
    instRef.current = selectedInstitutionId;

    const directory = useMemo<RoleDirectory>(
        () => roleDirectory ?? createRoleDirectory(() => instRef.current, (id, keys) => RoleService.rolesWithKeys(id, keys)),
        [roleDirectory],
    );

    // Catalogue is loaded the first time help mode is switched on, never before. A pick made while it is
    // still loading (or after a failed load) is parked and replayed when the catalogue arrives.
    const pendingRef = useRef<((c: HelpCatalogue) => void) | null>(null);
    const failedRef = useRef(false); // the last load failed (independent of the card being dismissed)
    const activeRef = useRef(false);
    activeRef.current = active;
    const [attempt, setAttempt] = useState(0);
    useEffect(() => {
        if (!active || catalogue) return;
        let cancelled = false;
        loadCatalogue().then(c => {
            if (cancelled) return;
            setCatalogue(c);
            setCatalogueError(false);
            const p = pendingRef.current;
            pendingRef.current = null;
            if (p) p(c);
        }).catch(() => { if (!cancelled) { failedRef.current = true; setCatalogueError(true); } });
        return () => { cancelled = true; };
    }, [active, catalogue, loadCatalogue, attempt]);

    const withCatalogue = useCallback((fn: (c: HelpCatalogue) => void) => {
        const c = catRef.current;
        if (c) { fn(c); return; }
        pendingRef.current = fn;
        if (failedRef.current) { failedRef.current = false; setCatalogueError(false); setAttempt(n => n + 1); }
    }, []);

    const exit = useCallback(() => {
        pendingRef.current = null;
        setActive(false); setTarget(null); setDragBox(null);
    }, []);
    const toggle = useCallback(() => { if (activeRef.current) exit(); else setActive(true); }, [exit]);

    const apply = useCallback((res: Resolution) => {
        setTarget(res.target);
        const path = normalizePath(window.location.pathname);
        const keys: (string | null)[] = res.target.kind === 'missing' && res.missingKeys.length === 0 ? [null] : res.missingKeys;
        for (const key of keys) {
            const id = `${key}|${path}`;
            if (loggedRef.current.has(id)) continue;
            loggedRef.current.add(id);
            logger.logMissing({ key, path });
        }
    }, [logger]);

    const pickAtPoint = useCallback((x: number, y: number) => withCatalogue(c => apply(resolveAtPoint(x, y, c))), [withCatalogue, apply]);

    const selectFeature = useCallback((el: HTMLElement) => {
        const r = el.getBoundingClientRect();
        withCatalogue(c => apply(resolveAtPoint((r.left + r.right) / 2, (r.top + r.bottom) / 2, c)));
    }, [withCatalogue, apply]);

    const explainElement = useCallback((el: HTMLElement) => withCatalogue(c => apply(resolveElement(el, c))), [withCatalogue, apply]);

    const explainSection = useCallback((s: SectionRef) => withCatalogue(c => apply(resolveSectionOf(s, c))), [withCatalogue, apply]);

    // Closing the card also clears a load error so the next pick retries.
    const clearTarget = useCallback(() => { setTarget(null); setCatalogueError(false); }, []);

    // Global listeners exist ONLY while help mode is on.
    const touchRef = useRef(touchMode);
    touchRef.current = touchMode;
    useEffect(() => {
        if (!active) return;
        const inUi = (t: EventTarget | null) => t instanceof Element && !!t.closest(`[${ATTR.ui}]`);
        let press: { x: number; y: number; type: string; dragging: boolean } | null = null;

        // Nothing outside the help UI may receive these: a pointerdown/mousedown would start a dnd-kit drag
        // (whose onDragEnd SAVES the board order) or open a MUI Select/Autocomplete; click/submit run app actions.
        const swallow = (e: Event) => {
            if (inUi(e.target)) return;
            e.preventDefault();
            e.stopPropagation();
        };
        // touchstart is only stopped, never preventDefault-ed, so the page can still scroll on a phone.
        const swallowTouch = (e: Event) => { if (!inUi(e.target)) e.stopPropagation(); };

        const onDown = (e: PointerEvent) => {
            if (inUi(e.target)) return;
            swallow(e);
            if (e.pointerType === 'mouse' && e.button !== 0) return;
            press = { x: e.clientX, y: e.clientY, type: e.pointerType, dragging: false };
        };
        const onMove = (e: PointerEvent) => {
            if (!press || press.type === 'touch' || touchRef.current) return;
            if (!press.dragging && Math.hypot(e.clientX - press.x, e.clientY - press.y) > DRAG_THRESHOLD) press.dragging = true;
            if (press.dragging) {
                setDragBox({
                    left: Math.min(press.x, e.clientX), top: Math.min(press.y, e.clientY),
                    right: Math.max(press.x, e.clientX), bottom: Math.max(press.y, e.clientY),
                });
            }
        };
        const onUp = (e: PointerEvent) => {
            const p = press;
            press = null;
            setDragBox(null);
            if (!p || (inUi(e.target) && !p.dragging)) return;
            if (p.dragging) {
                const box = {
                    left: Math.min(p.x, e.clientX), top: Math.min(p.y, e.clientY),
                    right: Math.max(p.x, e.clientX), bottom: Math.max(p.y, e.clientY),
                };
                withCatalogue(c => apply(resolveBox(box, c)));
            } else if (p.type === 'touch' || touchRef.current) {
                withCatalogue(c => apply(resolveAtPoint(e.clientX, e.clientY, c)));
            } else {
                withCatalogue(c => apply(resolveSectionAtPoint(e.clientX, e.clientY, c)));
            }
        };
        const onCancel = () => { press = null; setDragBox(null); };

        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                // Leave help mode only; stop it reaching an underlying MUI Dialog/Menu.
                e.stopPropagation();
                e.preventDefault();
                exit();
                return;
            }
            if (inUi(e.target)) return;
            if (e.key === 'Tab') return; // keyboard users still need to move focus
            e.preventDefault();
            e.stopPropagation();
            if (e.key === 'Enter' && e.target instanceof Element) {
                const el = e.target.closest<HTMLElement>(`[${ATTR.element}]`);
                if (el) explainElement(el);
            }
        };

        const swallowed = ['click', 'dblclick', 'auxclick', 'submit', 'mousedown', 'mouseup', 'dragstart', 'selectstart', 'contextmenu'];
        swallowed.forEach(n => document.addEventListener(n, swallow, true));
        document.addEventListener('touchstart', swallowTouch, true);
        document.addEventListener('pointerdown', onDown, true);
        document.addEventListener('pointermove', onMove, true);
        document.addEventListener('pointerup', onUp, true);
        document.addEventListener('pointercancel', onCancel, true);
        document.addEventListener('keydown', onKey, true);
        const prevSelect = document.body.style.userSelect;
        document.body.style.userSelect = 'none';
        return () => {
            swallowed.forEach(n => document.removeEventListener(n, swallow, true));
            document.removeEventListener('touchstart', swallowTouch, true);
            document.removeEventListener('pointerdown', onDown, true);
            document.removeEventListener('pointermove', onMove, true);
            document.removeEventListener('pointerup', onUp, true);
            document.removeEventListener('pointercancel', onCancel, true);
            document.removeEventListener('keydown', onKey, true);
            document.body.style.userSelect = prevSelect;
        };
    }, [active, apply, exit, withCatalogue, explainElement]);

    const canUse = useCallback(
        (keys: string[] | undefined, all?: boolean) => !keys || keys.length === 0 || (all ? keys.every(k => hasPermission(k)) : keys.some(k => hasPermission(k))),
        [hasPermission],
    );

    const value = useMemo<HelpContextValue>(() => ({
        active, toggle, exit, touchMode, setTouchMode, target, dragBox, catalogue, catalogueError, canUse,
        roleDirectory: directory, pickAtPoint, selectFeature, explainElement, explainSection, clearTarget,
    }), [active, toggle, exit, touchMode, target, dragBox, catalogue, catalogueError, canUse, directory, pickAtPoint, selectFeature, explainElement, explainSection, clearTarget]);

    return <HelpContext.Provider value={value}>{children}</HelpContext.Provider>;
};
